import { Link } from 'react-router-dom'
import { FileText, Search, Zap, BookOpen } from 'lucide-react'

interface SiteLinksProps {
  variant?: 'sidebar' | 'footer' | 'inline'
  className?: string
}

export function SiteLinks({ variant = 'inline', className = '' }: SiteLinksProps) {
  const mainPages = [
    {
      title: 'llm.txt Generator',
      href: '/generator',
      description: 'Create your llm.txt file in minutes',
      icon: Zap
    },
    {
      title: 'File Verification',
      href: '/verification',
      description: 'Check if your llm.txt file is working',
      icon: Search
    },
    {
      title: 'What is llm.txt?',
      href: '/what-is-llm-txt',
      description: 'Complete guide to llm.txt files',
      icon: BookOpen
    },
    {
      title: 'AI SEO Blog',
      href: '/blog',
      description: 'Latest insights and best practices',
      icon: FileText
    }
  ]

  const contentPages = [
    { title: 'Benefits of llm.txt', href: '/benefits' },
    { title: 'Implementation Guide', href: '/implementation' },
    { title: 'Best Practices', href: '/best-practices' },
    { title: 'Common Mistakes', href: '/common-mistakes' },
    { title: 'AI Tools Compatibility', href: '/ai-tools' },
    { title: 'SEO Impact', href: '/seo-impact' }
  ]

  if (variant === 'sidebar') {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Explore More</h3>
        <div className="space-y-3">
          {mainPages.map((page) => {
            const Icon = page.icon
            return (
              <Link
                key={page.href}
                to={page.href}
                className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors group"
              >
                <Icon className="h-5 w-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-gray-900 group-hover:text-primary-600">
                    {page.title}
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">{page.description}</p>
                </div>
              </Link>
            )
          })}
        </div>
      </div>
    )
  }

  if (variant === 'footer') {
    return (
      <div className={className}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
              Main Tools
            </h3>
            <ul className="space-y-3">
              {mainPages.map((page) => (
                <li key={page.href}>
                  <Link
                    to={page.href}
                    className="text-gray-600 hover:text-primary-600 transition-colors"
                  >
                    {page.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
              Learn More
            </h3>
            <ul className="space-y-3">
              {contentPages.map((page) => (
                <li key={page.href}>
                  <Link
                    to={page.href}
                    className="text-gray-600 hover:text-primary-600 transition-colors"
                  >
                    {page.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    )
  }

  // Inline variant (for content pages)
  return (
    <div className={`bg-gray-50 rounded-lg p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Related Resources</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {mainPages.slice(0, 4).map((page) => {
          const Icon = page.icon
          return (
            <Link
              key={page.href}
              to={page.href}
              className="flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors group"
            >
              <Icon className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-gray-900 group-hover:text-primary-600 text-sm">
                  {page.title}
                </h4>
                <p className="text-xs text-gray-600 mt-1">{page.description}</p>
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}

// Quick links component for content pages
export function QuickLinks({ className = '' }: { className?: string }) {
  const quickLinks = [
    { title: 'Create llm.txt', href: '/generator', primary: true },
    { title: 'Verify File', href: '/verification' },
    { title: 'Read Blog', href: '/blog' },
    { title: 'Learn More', href: '/what-is-llm-txt' }
  ]

  return (
    <div className={`flex flex-wrap gap-3 ${className}`}>
      {quickLinks.map((link) => (
        <Link
          key={link.href}
          to={link.href}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            link.primary
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {link.title}
        </Link>
      ))}
    </div>
  )
}
